import type { UnitAccessGrant } from '@/utils/unitAccess';

export enum Role {
  ADMIN = 'ADMIN',
  MEDICO = 'MEDICO',
  SECRETARIA = 'SECRETARIA',
  RECEPCIONISTA = 'RECEPCIONISTA',
  FINANCEIRO = 'FINANCEIRO',
  /**
   * Parceiro externo que cadastra pacientes e marca consultas em nome
   * deles. Acessa apenas o "Painel do Agendador" (lista de pacientes
   * próprios + status dos agendamentos próprios + cadastro de novo
   * paciente/consulta). Não enxerga prontuário, financeiro, prescrição
   * ou agendamentos de outros agendadores.
   */
  AGENDADOR_EXTERNO = 'AGENDADOR_EXTERNO',
  /**
   * Conta dedicada ao totem de auto-atendimento da recepção. Loga uma
   * vez no dispositivo físico e fica restrita a `/totem*` — qualquer
   * outra rota redireciona pra `/totem`. Permite ao próprio paciente
   * fazer cadastro/check-in via documento com foto e entrar na fila
   * sem intervenção da recepção.
   */
  TOTEM = 'TOTEM'
}

/**
 * Mapa de permissões finas por módulo. As chaves de ação seguem o catálogo
 * (`src/config/permissionCatalog.ts`): 'read' | 'create' | 'update' | 'delete'
 * (Ver / Incluir / Alterar / Remover). Todas opcionais — ausência = não concedida.
 */
export interface Permissions {
  [module: string]: {
    read?: boolean;
    create?: boolean;
    update?: boolean;
    delete?: boolean;
  };
}

export interface CustomClaims {
  role: Role;
  clinicId: string;
  permissions?: Permissions;
  unitIds?: string[];
}

export interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  role: Role;
  clinicId: string;
  permissions: Permissions;
  unitIds: string[];
  /**
   * De quais médicos este perfil pode ver e alterar a agenda. Vazio =
   * todos (o caso de quase todo mundo). Vem do doc do profissional, não
   * das claims: as claims só mudam no próximo login, e escopo de acesso
   * precisa valer assim que o admin salva.
   *
   * O corte de verdade está nas firestore rules — isto aqui é o que faz
   * a tela parar de oferecer o que ela não pode fazer.
   */
  allowedProfessionalIds: string[];
  /** Local de atendimento desta conta (recepção de cada lugar tem o seu).
   *  Filtra os pop-ups vindos de outro lugar — ver utils/serviceLocal. */
  serviceLocal?: string;
  /** Unidades que esta conta acessa, autorizadas por admin/dono (spec 019).
   *  Ausente = onde atende — regra em utils/unitAccess. Lido do doc do
   *  profissional, como o escopo de agenda. */
  unitAccess?: UnitAccessGrant;
}
